import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import { LightTooltip } from 'components/tooltips/LightTooltip';
import { useHourlyRates } from 'hooks/useHourlyRates';
import { useMemo } from 'react';
import { JobRoleGroupType } from 'types/jobRoles';
import { Pricelist } from 'types/pricelists';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { CopyHourlyRatesToClipboard } from './CopyHourlyRatesToClipboard';
import { HourlyRateDetailsCard } from './HourlyRateDetailsCard';
import { LabelButtons } from './buttons/LabelButtons';
import { inForcePeriodOf, isEmployeePriced, pricedEmployeeRows } from './modal/employeePricing';
import { useToday } from './modal/useEmployeePricing';
import { EMPLOYEE_CLIPBOARD_GROUP, employeeClipboardGroups } from './util';

interface Props {
    pricelist?: Pricelist;
    simpleView?: boolean;
}

export const PricelistDetailsCard = ({ pricelist, simpleView }: Props): JSX.Element => {
    const today = useToday();
    const isEmployeeList = isEmployeePriced(pricelist);
    // An employee-priced list shows the people of the period in force today.
    const employeeRates = useMemo(
        () => (isEmployeeList ? pricedEmployeeRows(inForcePeriodOf(pricelist?.periods, today)?.rates) : undefined),
        [isEmployeeList, pricelist?.periods, today]
    );
    const { hourlyRateDetails, hourlyRateSummary } = useHourlyRates(
        isEmployeeList ? [] : (pricelist?.rates ?? []),
        pricelist,
        employeeRates
    );
    const currency = pricelist?.currencyTypeCode ? `${pricelist.currencyTypeCode} ` : '';
    const { classifiers } = useClassifiers({ codes: ['JOB_ROLE_GROUP_TYPE'] });

    const groupTitles = useMemo(
        () => ({
            [JobRoleGroupType.LEGAL]: getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.LEGAL),
            [JobRoleGroupType.ADMIN]: getClassifierText(classifiers?.['JOB_ROLE_GROUP_TYPE'], JobRoleGroupType.ADMIN),
            [EMPLOYEE_CLIPBOARD_GROUP]: t('pricelists.employee.clipboard-group'),
        }),
        [classifiers]
    );

    return (
        <div className="text-grey-600 flex flex-col justify-start whitespace-normal">
            {pricelist && !simpleView && (
                <LabelButtons labels={pricelist.labelsList || []} pricingKind={pricelist.pricingKind} />
            )}
            {pricelist && !simpleView && <div className="mr-2 font-medium">{pricelist.name}</div>}
            <LightTooltip
                title={
                    <HourlyRateDetailsCard data={hourlyRateDetails} employeeRates={employeeRates} fontSize="0.8rem" />
                }
            >
                <div className="hover:text-link flex w-[100px] cursor-default items-center whitespace-nowrap hover:underline">
                    {currency}
                    {hourlyRateSummary}
                    <InfoOutlinedIcon className="text-grey-400 ml-px h-[1.2rem]" />
                    <CopyHourlyRatesToClipboard
                        data={hourlyRateDetails}
                        groupTitles={groupTitles}
                        extraGroups={employeeRates ? employeeClipboardGroups(employeeRates) : undefined}
                    />
                </div>
            </LightTooltip>
        </div>
    );
};
